import React, { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import { DataGrid, GridToolbar, gridClasses } from "@mui/x-data-grid";
import { Box } from "@mui/material";
import moment from "moment";
import Loader from "../loader/loader";
import infositeAPI from "../../services/infositeapi";
import DynamicBreadcrumb from "../header/breadcrumb";

const CareerEnquiries = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
    const [filterModel, setFilterModel] = useState({});
    const [filters, setFilters] = useState({ fromDate: "", toDate: "" });
    const [loaderVisible, setLoaderVisible] = useState(false);

    const updateFilters = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const fetchData = useCallback(async () => {
        setLoaderVisible(true);
        setLoading(true);
        try {
            const { page, pageSize } = paginationModel;
            const response = await infositeAPI.getCareerEnquiries({
                limit: pageSize,
                page,
                fromDate: filters.fromDate,
                toDate: filters.toDate,
                filterModel,
            });

            const data = response?.data?.result || [];
            const total = response?.data?.totalRecords?.[0]?.total_records || 0;

            const formattedRows = data.map((item, index) => ({
                id: item.id,
                sno: index + 1 + page * pageSize,
                fullName: item.fullName,
                contactNumber: item.contactNumber,
                email: item.email,
                jobCategory: item.jobCategory,
                address: item.address,
                message: item.message,
                resumeFile: item.resumeFile,
                photoFile: item.photoFile,
                createdAt: moment(item.createdAt).format("YYYY-MM-DD HH:mm"),
            }));

            setRows(formattedRows);
            setTotalRecords(total);
        } catch (error) {
            console.error("Error fetching career enquiries:", error);
        } finally {
            setLoaderVisible(false);
            setLoading(false);
        }
    }, [paginationModel, filters, filterModel]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const exportData = async () => {
        try {
            setLoaderVisible(true);
            const response = await infositeAPI.getCareerEnquiries({
                limit: totalRecords,
                page: 0,
                fromDate: filters.fromDate,
                toDate: filters.toDate,
                filterModel,
            });

            const allRows = response?.data?.result || [];
            const csvRows = [
                ["S.No", "Full Name", "Contact", "Email", "Job Category", "Address", "Message", "Resume", "Photo", "Created At"],
            ];

            allRows.forEach((item, index) => {
                csvRows.push([
                    index + 1,
                    item.fullName,
                    item.contactNumber,
                    item.email,
                    item.jobCategory,
                    item.address,
                    item.message,
                    item.resumeFile || "-",
                    item.photoFile || "-",
                    moment(item.createdAt).format("YYYY-MM-DD HH:mm"),
                ]);
            });

            const csvContent = csvRows.map((e) => e.join(",")).join("\n");
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.setAttribute("href", url);
            link.setAttribute("download", "career_enquiries.csv");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Export failed:", error);
        } finally {
            setLoaderVisible(false);
        }
    };

    const columns = [
        { field: "sno", headerName: "S.No", width: 80 },
        { field: "fullName", headerName: "Full Name", flex: 1 },
        { field: "contactNumber", headerName: "Contact", flex: 1 },
        { field: "email", headerName: "Email", flex: 1 },
        { field: "jobCategory", headerName: "Job Category", flex: 1 },
        { field: "address", headerName: "Address", flex: 2 },
        { field: "message", headerName: "Message", flex: 2 },
        {
            field: "resumeFile",
            headerName: "Resume",
            flex: 1,
            renderCell: (params) =>
                params.value ? (
                    <a href={`${process.env.REACT_APP_IMAGE_URL}/${params.value}`} target="_blank" rel="noopener noreferrer">
                        View
                    </a>
                ) : (
                    "-"
                ),
        },
        {
            field: "photoFile",
            headerName: "Photo",
            flex: 1,
            renderCell: (params) =>
                params.value ? (
                    <a href={`${process.env.REACT_APP_IMAGE_URL}/${params.value}`} target="_blank" rel="noopener noreferrer">
                        View
                    </a>
                ) : (
                    "-"
                ),
        },
        { field: "createdAt", headerName: "Created At", flex: 1 },
    ];

    const pathArray = [{ name: "Career Enquiries", url: null }];

    return (
        <Container className="mt-4 mb-4">
            <DynamicBreadcrumb pathArray={pathArray} />
            <h5>Career Enquiries</h5>

            <Form className="mb-3">
                <Row>
                    <Col md={3}>
                        <Form.Label>From Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={filters.fromDate}
                            onChange={(e) => updateFilters("fromDate", e.target.value)}
                        />
                    </Col>
                    <Col md={3}>
                        <Form.Label>To Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={filters.toDate}
                            onChange={(e) => updateFilters("toDate", e.target.value)}
                        />
                    </Col>
                    <Col md={3} className="d-flex align-items-end">
                        <Button variant="primary" onClick={fetchData}>Filter</Button>
                        <Button variant="success" className="ms-2" onClick={exportData}>Export</Button>
                    </Col>
                </Row>
            </Form>

            {loaderVisible && <Loader />}

            <Box>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    rowCount={totalRecords}
                    pageSizeOptions={[5, 10, 15, 20]}
                    paginationMode="server"
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    getRowHeight={() => "auto"}
                    filterMode="server"
                    onFilterModelChange={setFilterModel}
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            csvOptions: { disableToolbarButton: true },
                            printOptions: { disableToolbarButton: true },
                        },
                    }}
                    sx={{ [`& .${gridClasses.cell}`]: { py: 1 } }}
                />
            </Box>
        </Container>
    );
};

export default CareerEnquiries;
